import React, { useState, useEffect, useCallback } from 'react';
import '../../styles/parent/RightContent.css';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from 'recharts';
import { getStudentByName, getStudentSubjectScoresForParent, getStudentTopicMasteryForParent } from '../../utils/parentStudentService';
import { getInterventionCount } from '../../utils/interventionService';

const sampleData = [
  { name: 'Mon', overall: 80, session: 60 },
  { name: 'Tue', overall: 78, session: 65 },
  { name: 'Wed', overall: 82, session: 70 },
  { name: 'Thu', overall: 85, session: 50 },
  { name: 'Fri', overall: 88, session: 75 },
  { name: 'Sat', overall: 90, session: 80 },
  { name: 'Sun', overall: 92, session: 85 },
];

const barData = [
  { subject: 'Math', score: 88 },
  { subject: 'Science', score: 92 },
  { subject: 'English', score: 85 },
  { subject: 'History', score: 80 },
  { subject: 'Art', score: 95 },
];

const barColors = ['#82ca9d', '#8884d8', '#ffc658', '#ff7f7f', '#8dd1e1'];

// Sample events data
const sampleEvents = {
  '2025-07-15': { type: 'meeting', title: 'Parent Meeting', color: '#ff6b6b' },
  '2025-07-20': { type: 'therapy', title: 'Speech Therapy', color: '#4ecdc4' },
  '2025-07-25': { type: 'assessment', title: 'Progress Assessment', color: '#45b7d1' },
  '2025-07-30': { type: 'meeting', title: 'IEP Review', color: '#96ceb4' },
};

const DynamicCalendar = ({ selectedDate, onDateSelect, events }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // 'month', 'week', 'day'

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    // Adjust for Monday as first day of week
    const adjustedStartingDay = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;
    
    const days = [];
    
    // Add previous month's days
    const prevMonth = new Date(year, month - 1, 0);
    const prevMonthDays = prevMonth.getDate();
    for (let i = adjustedStartingDay - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthDays - i),
        isCurrentMonth: false,
        isToday: false
      });
    }
    
    // Add current month's days
    for (let i = 1; i <= daysInMonth; i++) {
      const dayDate = new Date(year, month, i);
      days.push({
        date: dayDate,
        isCurrentMonth: true,
        isToday: dayDate.toDateString() === new Date().toDateString()
      });
    }
    
    // Add next month's days to complete the grid
    const remainingDays = 42 - days.length; // 6 rows * 7 days
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false,
        isToday: false
      });
    }
    
    return days;
  };

  const formatDateKey = (date) => {
    return date.toISOString().split('T')[0];
  };

  const getEventForDate = (date) => {
    const dateKey = formatDateKey(date);
    return events[dateKey];
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const navigateYear = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setFullYear(prev.getFullYear() + direction);
      return newDate;
    });
  };

  const days = getDaysInMonth(currentDate);
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return (
    <div className="dynamic-calendar">
      <div className="calendar-header">
        <div className="calendar-nav">
          <button onClick={() => navigateYear(-1)} className="nav-btn">«</button>
          <button onClick={() => navigateMonth(-1)} className="nav-btn">‹</button>
        </div>
        <h2 className="calendar-title">
          {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
        </h2>
        <div className="calendar-nav">
          <button onClick={() => navigateMonth(1)} className="nav-btn">›</button>
          <button onClick={() => navigateYear(1)} className="nav-btn">»</button>
        </div>
      </div>
      
      <div className="calendar-grid">
        <div className="calendar-weekdays">
          {['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'].map(day => (
            <div key={day} className="weekday-header">{day}</div>
          ))}
        </div>
        
        <div className="calendar-days">
          {days.map((day, index) => {
            const event = getEventForDate(day.date);
            const isSelected = selectedDate && day.date.toDateString() === selectedDate.toDateString();
            const isWeekend = day.date.getDay() === 0 || day.date.getDay() === 6;
            
            return (
              <div
                key={index}
                className={`calendar-day ${!day.isCurrentMonth ? 'other-month' : ''} ${day.isToday ? 'today' : ''} ${isSelected ? 'selected' : ''} ${isWeekend ? 'weekend' : ''}`}
                onClick={() => onDateSelect(day.date)}
              >
                <span className="day-number">{day.date.getDate()}</span>
                {event && (
                  <div 
                    className="event-indicator"
                    style={{ backgroundColor: event.color }}
                    title={event.title}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
      
      <div className="calendar-controls">
        <button 
          className={`view-btn ${viewMode === 'day' ? 'active' : ''}`}
          onClick={() => setViewMode('day')}
        >
          Day View
        </button>
        <button 
          className={`view-btn ${viewMode === 'week' ? 'active' : ''}`}
          onClick={() => setViewMode('week')}
        >
          Week View
        </button>
        <button className="reminder-btn app-btn">
          Set reminders
        </button>
      </div>
    </div>
  );
};

const RightContent = ({ studentName }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [events] = useState(sampleEvents);
  
  // Student data states
  const [studentData, setStudentData] = useState(null);
  const [subjectData, setSubjectData] = useState([]);
  const [loadingSubjects, setLoadingSubjects] = useState(true);
  const [topicData, setTopicData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [filteredChartData, setFilteredChartData] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(true);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [interventionCount, setInterventionCount] = useState(0);
  const [studentNotLinked, setStudentNotLinked] = useState(false);

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    // You can add additional logic here for date selection
    console.log('Selected date:', date);
  };

  // Fetch student data by name
  const fetchStudentData = useCallback(async (studentNameParam) => {
    try {
      console.log('👤 Fetching student data for parent dashboard challenge...');
      console.log('📝 Student name param:', studentNameParam);
      
      // Use the student name passed from parent component
      if (!studentNameParam) {
        console.warn('⚠️ No student name provided, waiting for profile to load...');
        return;
      }
      
      console.log('🔍 Calling getStudentByName with:', studentNameParam);
      const student = await getStudentByName(studentNameParam);
      console.log('📊 getStudentByName result:', student);
      
      if (student) {
        setStudentNotLinked(false);
        setStudentData(student);
        console.log('✅ Student found for parent:', student.name, student.user_id);
        
        // Fetch subject scores for this specific student
        await fetchStudentSubjectScores(student.user_id);
        // Fetch topic mastery for this specific student
        await fetchStudentTopicMastery(student.user_id);
        // Fetch intervention count for this specific student
        await fetchInterventionCount(student.user_id);
      } else {
        console.warn('⚠️ No matching student found for this parent');
        showNoStudentLinked();
      }
    } catch (error) {
      console.error('❌ Error fetching student data for parent:', error);
      showNoStudentLinked();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch student data when studentName becomes available
  useEffect(() => {
    console.log('🔄 RightContent useEffect triggered. studentName:', studentName);
    if (studentName === '') {
      // The parent profile has no student name (or couldn't be loaded)
      showNoStudentLinked();
    } else if (studentName) {
      console.log('📊 Student name received, fetching data for:', studentName);
      fetchStudentData(studentName);
    } else {
      console.log('⏳ No student name yet, waiting...');
    }
  }, [studentName, fetchStudentData]);

  // Fetch intervention count for specific student
  const fetchInterventionCount = async (studentUserId) => {
    try {
      console.log('🎯 Fetching intervention count for student:', studentUserId);
      const count = await getInterventionCount(studentUserId);
      setInterventionCount(count);
      console.log('✅ Intervention count fetched:', count);
    } catch (error) {
      console.error('❌ Error fetching intervention count:', error);
      setInterventionCount(0); // Set to 0 on error
    }
  };

  // Fetch subject scores for specific student
  const fetchStudentSubjectScores = async (studentUserId) => {
    try {
      console.log('📊 Fetching subject scores for student:', studentUserId);
      setLoadingSubjects(true);
      
      const data = await getStudentSubjectScoresForParent(studentUserId);
      if (data && data.subject_ranking && data.subject_ranking.length > 0) {
        // Transform data to match chart format
        const defaultColors = ['#4a148c', '#6a1b9a', '#2e7d32', '#1565c0', '#e65100'];
        const transformedData = data.subject_ranking.map((subject, index) => ({
          subject: subject.subject,
          score: Math.round(subject.average_score * 10), // Convert to 0-100 scale
          color: defaultColors[index % defaultColors.length],
          total_score: subject.total_score,
          total_quizzes: subject.total_quizzes,
          accuracy: Math.round(subject.accuracy)
        }));
        
        setSubjectData(transformedData);
        console.log('✅ Subject scores loaded for parent:', transformedData);
      } else {
        console.log('📊 No subject data available for this student');
        setSubjectData([]);
      }
    } catch (error) {
      console.error('❌ Error fetching student subject scores:', error);
      setSubjectData([]);
    } finally {
      setLoadingSubjects(false);
    }
  };

  // Fetch topic mastery for specific student
  const fetchStudentTopicMastery = async (studentUserId) => {
    try {
      console.log('📊 Fetching topic mastery for student:', studentUserId);
      setLoadingTopics(true);
      
      const data = await getStudentTopicMasteryForParent(studentUserId);
      if (data && data.topic_mastery && data.topic_mastery.length > 0) {
        setTopicData(data.topic_mastery);
        const chartData = transformDataForChart(data.topic_mastery);
        setChartData(chartData);
        
        // Extract unique subjects
        const subjects = [...new Set(data.topic_mastery.map(item => item.subject))];
        setAvailableSubjects(subjects);
        setSelectedSubjects(subjects);
        setFilteredChartData(chartData);
        
        console.log('✅ Topic mastery loaded for parent:', data.topic_mastery);
      } else {
        console.log('📊 No topic mastery data available for this student');
        setTopicData([]);
        setChartData([]);
        setFilteredChartData([]);
      }
    } catch (error) {
      console.error('❌ Error fetching student topic mastery:', error);
      setTopicData([]);
      setChartData([]);
      setFilteredChartData([]);
    } finally {
      setLoadingTopics(false);
    }
  };

  // No matching student — clear everything rather than showing sample data
  const showNoStudentLinked = () => {
    setStudentData(null);
    setStudentNotLinked(true);
    setLoadingSubjects(false);
    setLoadingTopics(false);
    setSubjectData([]);
    setTopicData([]);
    setChartData([]);
    setFilteredChartData([]);
    setAvailableSubjects([]);
    setSelectedSubjects([]);
    setInterventionCount(0);
  };

  // Transform topic data into chart data for line chart (same logic as student dashboard)
  const transformDataForChart = (data) => {
    if (!data || data.length === 0) return [];

    const topicProgressMap = {};
    
    data.forEach(subject => {
      subject.topics.forEach(topic => {
        const key = `${subject.subject} - ${topic.topic}`;
        if (!topicProgressMap[key]) {
          const totalPossibleMarks = topic.total_quizzes * 10;
          const marksScored = topic.total_score;
          
          topicProgressMap[key] = {
            name: topic.topic,
            accuracy: topic.accuracy,
            totalQuizzes: topic.total_quizzes,
            correctQuizzes: topic.correct_quizzes,
            marksScored: marksScored,
            totalPossibleMarks: totalPossibleMarks,
            subject: subject.subject,
            topic: topic.topic
          };
        }
      });
    });

    return Object.values(topicProgressMap).sort((a, b) => b.marksScored - a.marksScored);
  };

  // Handle subject selection change
  const handleSubjectChange = (e) => {
    const value = e.target.value;
    if (value === 'all') {
      setSelectedSubjects(availableSubjects);
    } else {
      setSelectedSubjects([value]);
    }
  };

  // Filter chart data when selected subjects change
  useEffect(() => {
    if (chartData.length > 0 && selectedSubjects.length > 0) {
      const filtered = chartData.filter(item => selectedSubjects.includes(item.subject));
      setFilteredChartData(filtered);
    } else {
      setFilteredChartData(chartData);
    }
  }, [selectedSubjects, chartData]);

  useEffect(() => {
    fetchStudentData();
  }, [fetchStudentData]);

  return (
    <div className="parent-right-content"> 
      <div className="right-board-layout">
        {studentNotLinked && (
          <div style={{ background: '#fff8e1', border: '2px solid #d8c9ab', borderRadius: '8px', padding: '12px 16px', margin: '5px', color: '#2c3e50' }}>
            <strong>No student linked yet.</strong>{' '}
            {studentName
              ? `We couldn't find exactly one student named "${studentName}". Check that the name in your profile matches your child's full name as they signed up.`
              : "Add your child's full name to your profile to see their progress here."}
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', width: '100%', padding: '5px' }}>
          {/* Subject Performance Ranking Section */}
          <div className="stacked-section" style={{ background: '#e8f5e9', border: '2px solid #d8c9ab', borderRadius: '8px', padding: '15px', minHeight: '350px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <h3 style={{ 
                margin: '0 0 1.5rem 0', 
                color: '#2c3e50', 
                textAlign: 'center', 
                fontSize: '1.5rem', 
                fontWeight: '700',
                textShadow: '1px 1px 2px rgba(0,0,0,0.1)',
                letterSpacing: '0.5px',
                borderBottom: '2px solid #d8c9ab',
                paddingBottom: '10px'
              }}>
                Subject Performance Ranking
                {studentData && (
                  <div style={{ fontSize: '1.05rem', fontWeight: '500', marginTop: '8px', color: '#5a6c7d' }}>
                    for {studentData.name}
                  </div>
                )}
              </h3>
              {loadingSubjects ? (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  height: 160,
                  color: '#666',
                  fontSize: '14px'
                }}>
                  Loading quiz scores...
                </div>
              ) : (
                            <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={subjectData} margin={{ top: 20, right: 10, left: 40, bottom: 20 }}>
                    <XAxis dataKey="subject" tick={{ fill: '#000000', fontSize: 12, fontWeight: 'bold' }} />
                    <YAxis domain={[0, 100]} label={{ value: 'Average Score', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#000000' } }} tick={{ fill: '#000000', fontSize: 12, fontWeight: 'bold' }} />
                  <Tooltip 
                    contentStyle={{ 
                        backgroundColor: '#ffffff',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        color: '#000000'
                      }}
                      cursor={false}
                      formatter={(value, name, props) => [
                        `${value}% (${props.payload.total_quizzes} quizzes, ${props.payload.accuracy}% accuracy)`,
                        'Average Score'
                      ]}
                      labelFormatter={(label) => `Subject: ${label}`}
                    />
                    <Bar dataKey="score" name="Average Score">
                      {subjectData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color || `hsl(${index * 120}, 70%, 50%)`} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
              {!loadingSubjects && subjectData.every(subject => subject.score === 0) && (
                <div style={{ 
                  textAlign: 'center', 
                  color: '#666', 
                  marginTop: '10px',
                  fontSize: '12px'
                }}>
                  No quiz data available yet
                </div>
              )}
            </div>

          {/* Performance Analysis Section */}
          <div className="stacked-section" style={{ background: '#C9FFE7', border: '2px solid #d8c9ab', borderRadius: '8px', padding: '15px', minHeight: '350px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <h3 style={{ 
                margin: '0 0 1.5rem 0', 
                color: '#2c3e50', 
                textAlign: 'center', 
                fontSize: '1.5rem', 
                fontWeight: '700',
                textShadow: '1px 1px 2px rgba(0,0,0,0.1)',
                letterSpacing: '0.5px',
                borderBottom: '2px solid #d8c9ab',
                paddingBottom: '10px'
              }}>
                Performance Analysis
                {studentData && (
                  <div style={{ fontSize: '1.05rem', fontWeight: '500', marginTop: '8px', color: '#5a6c7d' }}>
                    for {studentData.name}
                  </div>
                )}
              </h3>
              {loadingTopics ? (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  height: 120,
                  color: '#666',
                  fontSize: '14px'
                }}>
                  Loading performance data...
                </div>
              ) : filteredChartData.length > 0 ? (
                            <div style={{ width: '100%', height: 200 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={filteredChartData} margin={{ top: 20, right: 20, left: 10, bottom: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip 
                        formatter={(value, name, props) => {
                          if (name === "Marks Scored") {
                            return [`${value} / ${props.payload.totalPossibleMarks}`, 'Marks'];
                          }
                          return value;
                    }} 
                  />
                  <Line 
                    type="monotone" 
                        dataKey="marksScored" 
                        stroke="#2ecc71" 
                    strokeWidth={3} 
                        dot={{ r: 4 }}
                        name="Marks Scored"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
              ) : (
                <div style={{ 
                  textAlign: 'center', 
                  color: '#666', 
                  fontSize: '12px'
                }}>
                  No performance data available yet
                </div>
              )}
            </div>



          {/* Behavior Report Section */}
          <div className="report-section" style={{ minHeight: '350px', display: 'flex', flexDirection: 'column' }}>
              <div className="section-header" onClick={() => console.log('Behavior Report clicked')} style={{ fontSize: '1.4rem' }}>
                <span className="section-icon" style={{ fontSize: '1.8rem' }}>📋</span>
                <span className="section-title" style={{ fontSize: '1.4rem', fontWeight: '600' }}>Behavior Report</span>
                <span className="section-arrow" style={{ fontSize: '1.8rem' }}>›</span>
              </div>
              <div className="section-content" style={{ fontSize: '1.15rem' }}>
                <div className="clickable-item" onClick={() => console.log('Positive behaviors clicked')} style={{ padding: '15px', fontSize: '1.15rem' }}>
                  <span className="item-icon" style={{ fontSize: '1.5rem' }}>✅</span>
                  <span className="item-text" style={{ fontSize: '1.15rem' }}>Positive behaviors</span>
                  <span className="item-count" style={{ fontSize: '1.15rem' }}>12</span>
                </div>
                <div className="clickable-item" onClick={() => console.log('Interventions clicked')} style={{ padding: '15px', fontSize: '1.15rem' }}>
                  <span className="item-icon" style={{ fontSize: '1.5rem' }}>🎯</span>
                  <span className="item-text" style={{ fontSize: '1.15rem' }}>Interventions</span>
                  <span className="item-count" style={{ fontSize: '1.15rem' }}>{interventionCount}</span>
                </div>
                <div className="clickable-item" onClick={() => console.log('Progress Report clicked')} style={{ padding: '15px', fontSize: '1.15rem' }}>
                  <span className="item-icon" style={{ fontSize: '1.5rem' }}>📊</span>
                  <span className="item-text" style={{ fontSize: '1.15rem' }}>Progress Report</span>
                  <span className="item-status available" style={{ fontSize: '1.15rem' }}>Download</span>
                </div>
              </div>
            </div>

          {/* Rewards Section */}
          <div className="stacked-section" style={{ background: '#fff5f5', border: '2px solid #d8c9ab', borderRadius: '8px', padding: '15px', minHeight: '350px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <h3 style={{ 
                margin: '0 0 1.5rem 0', 
                color: '#2c3e50', 
                textAlign: 'center', 
                fontSize: '1.5rem', 
                fontWeight: '700',
                textShadow: '1px 1px 2px rgba(0,0,0,0.1)',
                letterSpacing: '0.5px',
                borderBottom: '2px solid #d8c9ab',
                paddingBottom: '10px'
              }}>Rewards</h3>
              
              {/* Sticker Grid */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(4, 1fr)', 
                gap: '0.5rem',
                marginBottom: '0.8rem'
              }}>
                {/* Earned Stickers */}
                <div style={{ 
                  fontSize: '2rem', 
                  textAlign: 'center',
                  padding: '12px',
                  background: 'linear-gradient(135deg, #fef5e7 0%, #fff 100%)',
                  borderRadius: '12px',
                  border: '3px solid #10b981',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                }} 
                onClick={() => console.log('Star sticker clicked - Show achievement details')}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.15) translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 8px 12px rgba(16, 185, 129, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                }}
                title="Star Achievement">
                  ⭐
                </div>
                <div style={{ 
                  fontSize: '2rem', 
                  textAlign: 'center',
                  padding: '12px',
                  background: 'linear-gradient(135deg, #fef5e7 0%, #fff 100%)',
                  borderRadius: '12px',
                  border: '2px solid #10b981',
                  cursor: 'pointer'
                }} onClick={() => console.log('Heart sticker clicked')}>
                  ❤️
                </div>
                <div style={{ 
                  fontSize: '2rem', 
                  textAlign: 'center',
                  padding: '12px',
                  background: 'linear-gradient(135deg, #fef5e7 0%, #fff 100%)',
                  borderRadius: '12px',
                  border: '2px solid #10b981',
                  cursor: 'pointer'
                }} onClick={() => console.log('Trophy sticker clicked')}>
                  🏆
                </div>
                <div style={{ 
                  fontSize: '2rem', 
                  textAlign: 'center',
                  padding: '12px',
                  background: 'linear-gradient(135deg, #fef5e7 0%, #fff 100%)',
                  borderRadius: '12px',
                  border: '2px solid #10b981',
                  cursor: 'pointer'
                }} onClick={() => console.log('Fire sticker clicked')}>
                  🔥
                </div>
                
                {/* Locked Stickers */}
                <div style={{ 
                  fontSize: '2rem', 
                  textAlign: 'center',
                  padding: '12px',
                  background: '#f3f4f6',
                  borderRadius: '12px',
                  border: '2px solid #d1d5db',
                  opacity: '0.5',
                  cursor: 'pointer'
                }} onClick={() => console.log('Locked sticker clicked')}>
                  🎯
                </div>
                <div style={{ 
                  fontSize: '2rem', 
                  textAlign: 'center',
                  padding: '12px',
                  background: '#f3f4f6',
                  borderRadius: '12px',
                  border: '2px solid #d1d5db',
                  opacity: '0.5',
                  cursor: 'pointer'
                }} onClick={() => console.log('Locked sticker clicked')}>
                  🌟
                </div>
                <div style={{ 
                  fontSize: '2rem', 
                  textAlign: 'center',
                  padding: '12px',
                  background: '#f3f4f6',
                  borderRadius: '12px',
                  border: '2px solid #d1d5db',
                  opacity: '0.5',
                  cursor: 'pointer'
                }} onClick={() => console.log('Locked sticker clicked')}>
                  🎨
                </div>
                <div style={{ 
                  fontSize: '2rem', 
                  textAlign: 'center',
                  padding: '12px',
                  background: '#f3f4f6',
                  borderRadius: '12px',
                  border: '2px solid #d1d5db',
                  opacity: '0.5',
                  cursor: 'pointer'
                }} onClick={() => console.log('Locked sticker clicked')}>
                  🚀
                </div>
              </div>

              {/* Progress Info */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                fontSize: '0.8rem'
              }}>
                <span style={{ color: '#666' }}>4/8 stickers earned</span>
                <button className="app-btn" style={{
                  background: '#8b5cf6',
                  color: 'white',
                  border: 'none',
                  padding: '0.3rem 0.6rem',
                  borderRadius: '4px',
                  fontSize: '0.7rem',
                  fontWeight: '500',
                  cursor: 'pointer'
                }} onClick={() => console.log('View all stickers clicked')}>
                  View All
                </button>
              </div>
            </div>

        </div>
      </div>
    </div>
  );
};

export default RightContent;